import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import { Shield, Target, Users, Award, Eye, Lightbulb, CheckCircle, ArrowRight, Scale, Briefcase, Clock, Zap } from 'lucide-react';
import { useState } from 'react';
import ContactPopup from '@/components/ContactPopup';
import { Button } from '@/components/ui/button';

const AboutUs = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const whyChooseUs = [
    {
      icon: Scale,
      title: 'Board-Certified IP Attorneys',
      description: 'Our legal team comprises board-certified intellectual property specialists with deep expertise across trademarks, copyrights, and patents. Every case is handled by a senior attorney — never outsourced.'
    },
    {
      icon: Zap,
      title: 'AI-Enhanced Filing Technology',
      description: 'Our proprietary technology combines machine learning with legal expertise to deliver faster, more accurate filings. Conflicts are identified before they become costly setbacks.'
    },
    {
      icon: Clock,
      title: 'Faster Processing Times',
      description: 'Our streamlined protocols and expedited filing systems get your application processed significantly faster than the industry average — without cutting corners on quality.'
    },
    {
      icon: Shield,
      title: 'Zero Malpractice Record',
      description: '18 years of flawless legal service. Every filing undergoes triple-verification by certified specialists, maintaining our perfect record of accuracy and client trust.'
    },
    {
      icon: Briefcase,
      title: 'Transparent, Flat-Fee Pricing',
      description: 'No billable-hour surprises. We offer clear, competitive flat-fee packages so you know exactly what you\'re paying upfront — from filing to final registration.'
    },
    {
      icon: Users,
      title: 'Dedicated Attorney Access',
      description: 'You get direct access to your senior attorney throughout the process. No call centers, no junior associates making critical decisions on your behalf.'
    }
  ];

  return (
    <>
      <SEO 
        title="About Initiate Trademark - Trusted Trademark & IP Law Firm"
        description="Learn about Initiate Trademark — a premier intellectual property law firm combining board-certified attorneys with AI-powered technology. 18 years of protecting brands, ideas, and businesses."
        keywords="about Initiate Trademark, IP law firm, trademark attorneys, intellectual property specialists, trusted law firm"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden bg-primary-600">
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="inline-block mb-4">
              <span className="bg-secondary-600 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                About Initiate Trademark
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="block">Where Legal Precision Meets</span>
              <span className="block mt-2 bg-transparent twhiesrent">
                Technological Innovationwhi
              </span>
            </h1>
            
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
              For 18 years, Initiate Trademark has redefined intellectual property law through the strategic convergence of elite legal expertise and cutting-edge technology — protecting businesses of every size with unmatched precision and speed.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: About the Company */}
      <section className="py-20 bg-white" data-testid="about-company-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="relative min-h-[250px] md:min-h-[400px] rounded-3xl shadow-2xl overflow-hidden order-first">
              <img
                src="https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?w=900&q=80"
                alt="Our team at work"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center space-y-6 order-last">
              <h2 className="text-4xl font-bold text-gray-900">About the Company</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Initiate Trademark was founded on a single belief: that the intellectual property legal industry was broken. Traditional firms prioritized billable hours over outcomes, sacrificed speed for bureaucracy, and treated clients as case numbers.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our founding team — former USPTO examiners, Silicon Valley tech entrepreneurs, and board-certified IP litigators — engineered a radically different approach. We built proprietary AI-powered search technology, implemented agile case management, and structured our practice to give every client direct senior attorney access without the corporate overhead.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Today, Initiate Trademark serves everyone from first-time entrepreneurs to Fortune 500 enterprises. What unites our clients is their refusal to accept substandard legal service and their insistence on measurable results backed by transparent processes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Our Vision and Mission */}
      <section className="py-20 bg-transparent" data-testid="vision-mission-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Vision & Mission</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that drive everything we do for our clients
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Vision Card */}
            <div className="bg-white rounded-3xl p-10 shadow-lg card-hover border border-gray-100">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-2xl mb-6">
                <Eye className="w-8 h-8 text-secondary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                To be the most trusted name in intellectual property law — a firm where every brand, idea, and innovation receives the level of protection it deserves, powered by technology and guided by integrity.
              </p>
            </div>

            {/* Mission Card */}
            <div className="bg-white rounded-3xl p-10 shadow-lg card-hover border border-gray-100">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary-100 rounded-2xl mb-6">
                <Target className="w-8 h-8 text-secondary-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                To make world-class trademark and IP protection accessible, affordable, and effortless for businesses of every size — combining board-certified legal expertise with cutting-edge technology to deliver results that exceed expectations.
              </p>
            </div>
          </div>

          {/* Core Values Strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
            {[
              { icon: Shield, label: 'Integrity', desc: 'Zero-tolerance for errors' },
              { icon: Lightbulb, label: 'Innovation', desc: 'AI-enhanced legal tech' },
              { icon: CheckCircle, label: 'Excellence', desc: 'Industry-leading results' },
              { icon: Users, label: 'Client-First', desc: 'Direct attorney access' }
            ].map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 card-hover">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-secondary-100 rounded-xl mb-3">
                    <Icon className="w-6 h-6 text-secondary-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{value.label}</h4>
                  <p className="text-sm text-gray-500">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Why Choose Us */}
      <section className="py-20 bg-white" data-testid="why-choose-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Initiate Trademark</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              What sets us apart from every other IP firm
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white p-8 rounded-2xl card-hover border border-gray-100 shadow-sm" data-testid={`why-choose-card-${idx}`}>
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-secondary-100 rounded-2xl mb-5">
                    <Icon className="w-7 h-7 text-secondary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4 & 5: Testimonials + FAQs (from Home Page component) */}
      <TestimonialFAQSection />

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-4xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-lg text-gray-300 mb-10 leading-relaxed">
            Get a free consultation with one of our board-certified IP attorneys. No obligation, no hidden fees — just expert guidance tailored to your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              
              className="js-chat bg-secondary-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-lg hover:border-white"
              data-testid="about-cta-btn"
            >
              Get Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} pageSource="about_us" />
    </>
  );
};

export default AboutUs;
